import React, { useState } from 'react';
import {
  Phone, Mail, MapPin, Download, FileText, CheckCircle2, Send,
  Bike, Car, BookOpen, Target, Users, Clock, Award, Shield, Sparkles,
  AlertCircle, Loader2, ArrowRight,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useSeo } from '../hooks/useSeo';
import PrestationDialog from '../components/PrestationDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const PHONE = '07 82 26 82 00';
const PHONE_TEL = '+33782268200';
const EMAIL = 'contact@code2roues.fr';

const PRESTATIONS = [
  {
    icon: Sparkles,
    title: 'Permis AM — Citroën Ami',
    desc: 'Quadricycle léger dès 14 ans. 8h de formation obligatoire, sans examen. Véhicule 100% électrique Citroën Ami fourni.',
    tag: 'AM',
    catalogCode: 'am',
    duration: '8h (1 journée)',
    priceFrom: '420 € TTC',
    priceLabel: 'Prix public conseillé',
    publicCible: 'Jeunes dès 14 ans souhaitant conduire un quadricycle léger (Citroën Ami, Aixam…).',
    keyPoints: [
      'Citroën Ami 100% électrique fournie',
      'Équipement complet inclus (casque, gants, gilet)',
      'Circuit fermé + voies ouvertes',
      'Attestation BSR / permis AM remise le jour même',
      "Rattachement sous votre agrément",
    ],
    legalRef: 'Arrêté du 8 janvier 2013 · Catégorie AM',
  },
  {
    icon: Bike,
    title: 'Permis A1 — Honda CB125',
    desc: 'Permis moto 125 cm³ dès 16 ans. 20h minimum (plateau + circulation). Honda CB125F ou CB125R.',
    tag: 'A1',
    catalogCode: 'a1',
    duration: '20h minimum',
    priceFrom: '1 190 € TTC',
    priceLabel: 'Prix public conseillé',
    publicCible: 'Candidats 16-18 ans visant A1 (125 cm³, ≤ 11 kW) ou extension depuis le B.',
    keyPoints: [
      'Honda CB125F ou CB125R fournie',
      '8h plateau homologué + 12h circulation',
      'Intercom radio pour coaching temps réel',
      'Préparation plateau + examen ETM',
      'Vêtements de protection fournis si besoin',
    ],
    legalRef: 'Arrêté 23 avril 2012 modifié · Catégorie A1',
  },
  {
    icon: Bike,
    title: 'Permis A2 — Yamaha MT-07',
    desc: 'Permis moto A2 dès 18 ans. 20h minimum (plateau + circulation). Yamaha MT-07 bridée A2 ou KTM 390 Duke.',
    tag: 'A2',
    catalogCode: 'a2',
    duration: '20h minimum',
    priceFrom: '1 290 € TTC',
    priceLabel: 'Prix public conseillé',
    publicCible: 'Candidats 18+ ans visant A2 (motos ≤ 35 kW). Passerelle A2→A après 2 ans.',
    keyPoints: [
      'Yamaha MT-07 bridée A2 (ou KTM 390 Duke)',
      '8h plateau + 12h circulation + débrief vidéo',
      "Trajectoires de sécurité, freinage d'urgence",
      'Passerelle A2→A accessible après 2 ans',
      'Préparation complète examen (plateau + ETM)',
    ],
    legalRef: 'Arrêté 23 avril 2012 modifié · Catégorie A2',
  },
  {
    icon: Award,
    title: 'Passerelle A2 → A',
    desc: "Formation 7h obligatoire conforme arrêté 1er juin 2016. 2h théorie + 2h plateau + 3h circulation. Certificat délivré.",
    tag: 'Passerelle A',
    catalogCode: 'passA',
    duration: '7h (1 journée)',
    priceFrom: '220 € / jour',
    priceLabel: 'À partir de',
    publicCible: "Titulaires du permis A2 depuis au moins 2 ans visant l'accès au permis A (motos > 35 kW).",
    keyPoints: [
      '2h théorie : accidentologie, physique moto',
      '2h plateau : prise en main machine puissante',
      '3h circulation : situation réelle',
      'Aucun examen final — attestation remise',
      'Accès obligatoire motos > 35 kW',
    ],
    legalRef: 'Arrêté du 1er juin 2016 · Art. L221-2 CR',
  },
  {
    icon: Sparkles,
    title: 'Formation 125 cc / L5e',
    desc: '7h obligatoires pour les titulaires du permis B (arrêté 7 janvier 2019). 1h théorie + 3h plateau + 3h circulation.',
    tag: '7h 125',
    catalogCode: 'pass125',
    duration: '7h (1 journée)',
    priceFrom: '220 € / jour',
    priceLabel: 'À partir de',
    publicCible: 'Titulaires du permis B depuis + de 2 ans souhaitant conduire une 125 cc ou un tricycle L5e.',
    keyPoints: [
      '1h théorie : spécificités 125, risques, équipement',
      '3h plateau : maniabilité, équilibre, freinage',
      '3h circulation : interaction trafic, trajectoires',
      'Aucun examen — attestation au verso du permis B',
      'Dispense possible : B < 1980 ou 125 attestée 2006-2010',
    ],
    legalRef: 'Arrêté du 7 janvier 2019 · Art. R221-8 CR',
  },
  {
    icon: Car,
    title: 'Permis B',
    desc: "Formation complète et sur mesure. Conduite voiture, évaluation initiale, accompagnement examen blanc. Véhicule de l'établissement.",
    tag: 'B',
    catalogCode: null,
    duration: 'À la carte',
    priceFrom: '30 € / h',
    priceLabel: 'À partir de',
    publicCible: "Élèves de l'auto-école en formation initiale, AAC ou perfectionnement avant examen.",
    keyPoints: [
      'Évaluation initiale normalisée (grille REMC)',
      'Conduite circulation + rond-points + voie rapide',
      'Préparation examen blanc + débrief P-A-D-A',
      'Compte-rendu écrit à chaque séance',
      "Véhicule et carburant fournis par l'auto-école",
    ],
    legalRef: 'Référentiel REMC — Arrêté 13 mai 2013',
  },
  {
    icon: Target,
    title: 'Plateau & Circulation',
    desc: 'Perfectionnement et préparation aux examens. Encadrement plateau homologué et circulation réelle, intercom radio.',
    tag: 'Tech',
    catalogCode: null,
    duration: 'À la séance (2-4h)',
    priceFrom: '32 € / h',
    priceLabel: 'À partir de',
    publicCible: "Élèves en difficulté sur un item spécifique ou en préparation finale d'examen.",
    keyPoints: [
      'Diagnostic précis des points à travailler',
      "Exercices ciblés : évitement, freinage d'urgence, slalom",
      "Simulations d'examen plateau en conditions réelles",
      'Débrief vidéo possible (sur demande)',
      'Intercom radio pour coaching continu en circulation',
    ],
    legalRef: 'Programme de formation REMC',
  },
  {
    icon: Users,
    title: 'Renfort auto-écoles',
    desc: "Remplacement, surcharge, besoin ponctuel. Vacation programmée 72h à l'avance ou renfort régulier hebdomadaire.",
    tag: 'Flex',
    catalogCode: null,
    duration: 'Vacation / Régulier',
    priceFrom: 'Devis selon volume',
    priceLabel: 'Sur mesure',
    publicCible: "Auto-écoles ayant besoin d'un enseignant qualifié ECSR+CCS 2 roues en remplacement ou en renfort.",
    keyPoints: [
      'Remplacement maladie / congés / formation moniteur',
      'Renfort surcharge (pic rentrée, pic examens)',
      'Vacation hebdo régulière (ex. tous les mardis)',
      'Reprise élèves en cours sans perte de progression',
      'Facturation mensuelle, règlement à 30 jours',
    ],
    legalRef: 'Micro-entreprise · TVA non applicable Art. 293 B CGI',
  },
  {
    icon: Shield,
    title: 'Sécurité routière',
    desc: 'Sensibilisation et perfectionnement. Ateliers RSR, modules thématiques (alcool, vitesse, premiers secours, environnement).',
    tag: 'RSR',
    catalogCode: null,
    duration: 'Modules 2h à 1 journée',
    priceFrom: 'Devis selon durée',
    priceLabel: 'Sur mesure',
    publicCible: 'Entreprises, collectivités, lycées, auto-écoles proposant des ateliers RSR.',
    keyPoints: [
      'Alcool, stupéfiants, médicaments : impacts sur la conduite',
      'Vitesse, distances de sécurité, physiologie du choc',
      'Premiers secours (sensibilisation PSC1)',
      'Éco-conduite et environnement',
      'Supports pédagogiques fournis (films, cas réels, quiz)',
    ],
    legalRef: 'Programme national RSR · DSR',
  },
];

// Normalise une prestation vers la shape attendue par PrestationDialog partagé
const toPrestation = (p) => ({
  code:         p.title.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
  catalogCode:  p.catalogCode,
  title:        p.title,
  subtitle:     p.desc,
  badge:        p.tag,
  duration:     p.duration,
  price:        p.priceFrom,
  priceLabel:   p.priceLabel,
  visual:       { kind: 'icon', Icon: p.icon },
  publicCible:  p.publicCible,
  keyPoints:    p.keyPoints,
  legalRef:     p.legalRef,
  accent:       'orange',
});

const VALEURS = [
  {
    icon: Users,
    title: 'Flexibilité',
    desc: 'Disponibilités adaptées à vos besoins',
  },
  {
    icon: Shield,
    title: 'Professionnalisme',
    desc: "Sérieux, ponctualité et qualité d'enseignement",
  },
  {
    icon: BookOpen,
    title: 'Pédagogie',
    desc: 'Approche individualisée et progressive',
  },
  {
    icon: Award,
    title: 'Résultats',
    desc: 'Une priorité : la réussite en toute sécurité',
  },
];

const POURQUOI = [
  'Double compétence B + 2 roues (rare sur le marché de la prestation)',
  'Expérience terrain et pédagogique, conforme au référentiel REMC',
  'Pédagogie moderne et adaptée à chaque profil d\'élève',
  'Matériel professionnel & méthodes efficaces',
  'Interventions flexibles et réactives — réponse rapide',
  'Utilisation des véhicules de l\'établissement',
  'Disponibilités selon vos besoins (vacation, renfort, remplacement)',
];

const TARIFS_PUBLICS = [
  { label: 'Heure Permis B', value: '30 €' },
  { label: 'Heure Moto A1 / A2', value: '32 €' },
  { label: 'Demi-journée moto (4h)', value: '120 €' },
  { label: 'Journée complète moto (7h)', value: '220 €' },
];

export default function PrestationsPro() {
  useSeo({
    title: 'Prestations Pro · Auto-écoles & Moto-écoles · Nord Isère & Lyon Est — Code 2 Roues',
    description:
      "Bruno Llorens, enseignant de la conduite indépendant (B & 2 roues), propose ses prestations aux auto-écoles et moto-écoles du Nord Isère et de l'Est lyonnais : Permis B, A1/A2, plateau, circulation, Passerelle A2→A, Formation 125, renfort, remplacement. La passion. La technique. La sécurité.",
    keywords: 'moniteur indépendant ECSR, prestation auto-école, prestation moto-école, remplacement moniteur moto, formation 7h Passerelle, formation 125, Nord Isère, Lyon Est, Bourgoin-Jallieu, Villefontaine, La Tour-du-Pin, enseignant conduite freelance, TP ECSR, CCS 2 roues',
    path: '/prestations-pro',
    noindex: true,
  });

  const [form, setForm] = useState({
    school_name: '',
    city: '',
    structure_type: 'auto-ecole',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    needs: '',
    dates: '',
    consent: false,
    website: '', // honeypot
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPrestation, setSelectedPrestation] = useState(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.consent) {
      setError('Merci de cocher le consentement RGPD avant d\'envoyer votre demande.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/prestations/devis`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || `Erreur ${res.status}`);
      }
      setSubmitted(true);
      toast.success('Demande envoyée !', {
        description: 'Bruno vous recontactera sous 48h ouvrées.',
      });
    } catch (err) {
      setError(err.message || 'Une erreur est survenue. Réessayez ou contactez directement par téléphone.');
      toast.error('Envoi impossible', { description: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-12 sm:space-y-16 pb-12 sm:pb-16">
      {/* ===== HERO ===== */}
      <section
        className="relative overflow-hidden rounded-2xl border bg-card"
        data-testid="prestations-hero"
      >
        <div className="absolute inset-x-0 top-0 h-[3px] bg-[hsl(var(--primary))]" />
        <div className="grid lg:grid-cols-[1.3fr_1fr] gap-0">
          {/* Bloc gauche : titre + intro */}
          <div className="p-6 sm:p-10 lg:p-14">
            <div className="inline-flex items-center gap-1.5 text-[10px] sm:text-[11px] font-black uppercase tracking-[0.22em] text-[hsl(var(--primary))] mb-3">
              <span className="w-6 h-px bg-[hsl(var(--primary))]" />
              Prestations Pro · 2026
            </div>
            <h1
              className="text-3xl sm:text-4xl lg:text-[3rem] font-black tracking-tight leading-[1.05] mb-4 [hyphens:none] [word-break:normal]"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
              data-testid="prestations-title"
            >
              <span className="whitespace-nowrap">Auto-écoles</span>{' '}&{' '}
              <span className="whitespace-nowrap">moto-écoles</span>
              <br />
              du <span className="whitespace-nowrap text-[hsl(var(--primary))]">Nord&nbsp;Isère</span>{' '}et de{' '}
              <span className="whitespace-nowrap text-[hsl(var(--primary))]">Lyon&nbsp;Est</span>,
              <br />
              je viens enseigner chez vous.
            </h1>
            <div className="text-xs sm:text-sm font-black uppercase tracking-[0.18em] text-foreground/60 mb-4">
              La passion. La technique. <span className="text-[hsl(var(--primary))]">La sécurité.</span>
            </div>
            <p className="text-base sm:text-lg text-foreground/75 leading-relaxed max-w-xl mb-6">
              <strong className="text-foreground">Bruno Llorens</strong>, formateur expérimenté et
              passionné. J'accompagne les <strong>élèves</strong> et les <strong>établissements</strong> avec
              exigence, pédagogie et bienveillance, pour des résultats durables en toute sécurité.
              Disponible en <strong>vacation, renfort ou remplacement</strong>.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                size="lg"
                className="h-12 px-6 rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 font-bold"
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                data-testid="prestations-hero-cta-devis"
              >
                <Send className="w-4 h-4 mr-2" />
                Demander un devis
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="h-12 px-6 rounded-full font-bold"
                asChild
                data-testid="prestations-hero-cta-phone"
              >
                <a href={`tel:${PHONE_TEL}`}>
                  <Phone className="w-4 h-4 mr-2" />
                  {PHONE}
                </a>
              </Button>
            </div>
          </div>

          {/* Bloc droit : 4 chiffres clés */}
          <div className="px-6 sm:px-8 py-8 sm:py-10 border-t lg:border-t-0 lg:border-l border-foreground/8 bg-gradient-to-br from-[hsl(var(--primary-soft))]/40 to-transparent">
            <div className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.22em] text-foreground/55 mb-5">
              En bref
            </div>
            <dl className="space-y-5">
              {[
                { dt: 'Zone d\'intervention', dd: 'Nord Isère & Lyon Est (Bourgoin, Villefontaine, La Tour-du-Pin)' },
                { dt: 'Catégories enseignées', dd: 'B · A1 · A2 · 125 cc · Passerelle A2→A' },
                { dt: 'Statut juridique', dd: 'Micro-entreprise · TVA non applicable (293 B CGI)' },
                { dt: 'Qualifications', dd: 'TP ECSR · CCS 2 Roues · Autorisation d\'enseigner valide' },
              ].map((it) => (
                <div key={it.dt} className="border-b border-foreground/8 pb-4 last:border-0 last:pb-0">
                  <dt className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1">{it.dt}</dt>
                  <dd className="text-sm sm:text-base font-bold text-foreground leading-snug">{it.dd}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      {/* ===== BLOC 1 — PRÉSENTATION + 4 VALEURS ===== */}
      <section data-testid="prestations-presentation">
        <div className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-[0.22em] text-[hsl(var(--primary))] mb-3">
          <span className="w-6 h-px bg-[hsl(var(--primary))]" />
          01 · Qui suis-je
        </div>
        <h2
          className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight leading-[1.15] mb-5 max-w-3xl"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          Un moniteur de terrain, à votre disposition pour combler vos besoins ponctuels ou récurrents.
        </h2>
        <div className="grid md:grid-cols-2 gap-6 lg:gap-10 max-w-5xl mb-10">
          <div className="space-y-4 text-foreground/80 leading-relaxed">
            <p>
              <strong className="text-foreground">Enseignant de la conduite et de la sécurité routière (ECSR)</strong> indépendant,
              installé en Isère. J'interviens en prestation pour les auto-écoles et moto-écoles du{' '}
              <strong className="text-foreground">Nord Isère et de l'Est lyonnais</strong>.
            </p>
            <p>
              Mon approche allie <strong className="text-foreground">exigence pédagogique</strong>,{' '}
              <strong className="text-foreground">professionnalisme</strong> et{' '}
              <strong className="text-foreground">bienveillance</strong> : chaque séance débouche sur une{' '}
              <strong>auto-évaluation argumentée</strong> de l'élève, jamais sur un simple
              « ça passe / ça passe pas ».
            </p>
            <p>
              Disponible en <strong>vacation ponctuelle, renfort régulier ou remplacement complet</strong>{' '}
              selon vos besoins. Je m'aligne sur votre méthode et votre image — l'élève ne doit pas
              ressentir de rupture pédagogique entre votre équipe en place et moi.
            </p>
            <blockquote className="border-l-4 border-[hsl(var(--primary))] pl-4 italic text-foreground/85 mt-5 font-medium">
              « Transmettre bien plus qu'une compétence, une vraie conduite pour la vie. »
            </blockquote>
          </div>
          <div className="grid grid-cols-2 gap-3 self-start">
            {VALEURS.map((v) => (
              <div
                key={v.title}
                className="rounded-xl border bg-card p-4 sm:p-5 text-center hover:border-[hsl(var(--primary))] hover:-translate-y-0.5 transition-all"
                data-testid={`valeur-${v.title.toLowerCase()}`}
              >
                <div className="w-10 h-10 rounded-lg grid place-items-center bg-[hsl(var(--primary))] text-white mx-auto mb-3">
                  <v.icon className="w-5 h-5" />
                </div>
                <div className="text-[10px] font-black uppercase tracking-widest text-[hsl(var(--primary))] mb-1">
                  {v.title}
                </div>
                <div className="text-xs sm:text-[13px] text-foreground/75 leading-snug">
                  {v.desc}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bandeau pourquoi me faire confiance */}
        <div className="rounded-2xl border bg-card p-5 sm:p-7" data-testid="prestations-pourquoi">
          <div className="text-[10px] font-black uppercase tracking-widest text-[hsl(var(--primary))] mb-2">
            Pourquoi me faire confiance ?
          </div>
          <h3 className="font-bold text-lg sm:text-xl mb-4 max-w-2xl">
            Une double compétence rare, une approche moderne, une réactivité réelle.
          </h3>
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2.5">
            {POURQUOI.map((t) => (
              <li key={t} className="flex items-start gap-2 text-sm">
                <CheckCircle2 className="w-4 h-4 text-[hsl(var(--primary))] flex-shrink-0 mt-0.5" />
                <span className="leading-snug">{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ===== BLOC 2 — PRESTATIONS ===== */}
      <section data-testid="prestations-services">
        <div className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-[0.22em] text-[hsl(var(--primary))] mb-3">
          <span className="w-6 h-px bg-[hsl(var(--primary))]" />
          02 · Mes prestations
        </div>
        <h2
          className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight leading-[1.15] mb-3 max-w-3xl"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          Catalogue complet B & 2 roues.
        </h2>
        <p className="text-foreground/70 mb-8 max-w-2xl">
          Toutes les prestations peuvent être combinées sur une même journée (ex. matin code en salle + après-midi
          plateau A2). Devis personnalisé selon volume et fréquence.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {PRESTATIONS.map((p) => (
            <button
              type="button"
              key={p.title}
              onClick={() => setSelectedPrestation(p)}
              className="group relative text-left rounded-xl border bg-card p-5 hover:border-[hsl(var(--primary))] hover:-translate-y-0.5 hover:shadow-lg transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))] focus-visible:ring-offset-2"
              data-testid={`prestation-card-${p.tag.toLowerCase()}`}
              aria-label={`Détails ${p.title}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 rounded-lg grid place-items-center bg-[hsl(var(--primary-soft))] group-hover:scale-110 transition-transform">
                  <p.icon className="w-5 h-5 text-[hsl(var(--primary))]" />
                </div>
                <Badge variant="outline" className="text-[10px] font-bold uppercase">
                  {p.tag}
                </Badge>
              </div>
              <h3 className="font-bold text-base leading-tight mb-2">{p.title}</h3>
              <p className="text-xs sm:text-[13px] text-foreground/70 leading-relaxed mb-3 line-clamp-3">{p.desc}</p>
              {/* Footer prix + CTA */}
              <div className="pt-3 border-t border-border/60 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-semibold">
                    À partir de
                  </div>
                  <div className="text-sm font-black text-[hsl(var(--primary))] tabular-nums truncate">
                    {p.priceFrom}
                  </div>
                </div>
                <span
                  className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-[hsl(var(--primary))] group-hover:gap-2 transition-all"
                  aria-hidden="true"
                >
                  Détails <ArrowRight className="w-3 h-3" />
                </span>
              </div>
            </button>
          ))}
        </div>

        {/* Mini-grille tarifs publics */}
        <div className="mt-8 rounded-xl border bg-card p-5 sm:p-7">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-[hsl(var(--primary))] mb-1">
                Tarifs publics 2026
              </div>
              <h3 className="font-bold text-lg sm:text-xl">Quelques repères tarifaires</h3>
            </div>
            <Button variant="outline" size="sm" asChild className="rounded-full" data-testid="prestations-grille-cta-inline">
              <a href={`${API_URL}/api/prestations/grille-tarifaire.pdf`} target="_blank" rel="noopener noreferrer">
                <Download className="w-3.5 h-3.5 mr-1.5" />
                Grille complète (PDF)
              </a>
            </Button>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {TARIFS_PUBLICS.map((t) => (
              <div key={t.label} className="rounded-lg border bg-background/40 p-3 text-center">
                <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1 leading-tight">
                  {t.label}
                </div>
                <div className="text-xl sm:text-2xl font-black text-[hsl(var(--primary))] tabular-nums">
                  {t.value}
                </div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground italic mt-4 leading-relaxed">
            Tarifs HT — micro-entreprise, TVA non applicable (art. 293 B du CGI). Tarifs hors carburant.
            <strong> Véhicules de l'établissement</strong> utilisés (aucune logistique supplémentaire).
            Formations 7h (Passerelle, 125) et modules en salle : journée complète à partir de 220 €.
            Facturation mensuelle, paiement à 30 jours.
          </p>
        </div>
      </section>

      {/* ===== BLOC 3 — TÉLÉCHARGEMENTS ===== */}
      <section
        className="rounded-2xl border bg-gradient-to-br from-[hsl(var(--primary-soft))]/50 to-transparent p-6 sm:p-10"
        data-testid="prestations-downloads"
      >
        <div className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-[0.22em] text-[hsl(var(--primary))] mb-3">
          <span className="w-6 h-px bg-[hsl(var(--primary))]" />
          03 · Téléchargements
        </div>
        <h2
          className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight leading-[1.15] mb-3 max-w-3xl"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          Tout ce qu'il faut pour décider.
        </h2>
        <p className="text-foreground/70 mb-8 max-w-2xl">
          Deux documents à télécharger librement. La convention de prestation détaillée vous est transmise sur
          demande, après un premier échange téléphonique ou par email.
        </p>

        <div className="grid sm:grid-cols-2 gap-4">
          {[
            {
              title: 'Dossier prestataire complet',
              desc: '3 pages : présentation, prestations détaillées, tarifs, zone d\'intervention, modalités. À transmettre à votre direction.',
              url: `${API_URL}/api/prestations/dossier-prestataire.pdf`,
              testid: 'download-dossier',
              icon: FileText,
            },
            {
              title: 'Grille tarifaire 2026',
              desc: '1 page synthèse : tous les tarifs publics, conditions de règlement, conditions kilométriques.',
              url: `${API_URL}/api/prestations/grille-tarifaire.pdf`,
              testid: 'download-grille',
              icon: Download,
            },
          ].map((d) => (
            <a
              key={d.title}
              href={d.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group rounded-xl border bg-card p-5 sm:p-6 hover:border-[hsl(var(--primary))] hover:-translate-y-0.5 hover:shadow-md transition-all flex items-start gap-4"
              data-testid={d.testid}
            >
              <div className="w-12 h-12 rounded-xl grid place-items-center bg-[hsl(var(--primary))] text-white flex-shrink-0">
                <d.icon className="w-5 h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <h3 className="font-bold text-base sm:text-lg leading-tight">{d.title}</h3>
                  <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-[hsl(var(--primary))] group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                </div>
                <p className="text-xs sm:text-sm text-foreground/70 leading-relaxed">{d.desc}</p>
                <div className="text-[10px] font-bold uppercase tracking-widest text-[hsl(var(--primary))] mt-2">
                  PDF · Format A4
                </div>
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* ===== BLOC 4 — CONTACT + FORMULAIRE ===== */}
      <section id="contact" data-testid="prestations-contact">
        <div className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-[0.22em] text-[hsl(var(--primary))] mb-3">
          <span className="w-6 h-px bg-[hsl(var(--primary))]" />
          04 · Demander un devis
        </div>
        <h2
          className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight leading-[1.15] mb-3 max-w-3xl"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          Premier échange, sans engagement.
        </h2>
        <p className="text-foreground/70 mb-8 max-w-2xl">
          Remplissez le formulaire ci-dessous ou contactez-moi directement. Je vous réponds personnellement
          sous <strong>48 heures ouvrées</strong> avec un devis adapté à votre besoin.
        </p>

        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-6">
          {/* Bloc contact direct */}
          <div className="space-y-4">
            <div className="rounded-xl border bg-card p-5 sm:p-6">
              <div className="text-[10px] font-black uppercase tracking-widest text-[hsl(var(--primary))] mb-3">
                Contact direct
              </div>
              <a
                href={`tel:${PHONE_TEL}`}
                className="flex items-center gap-3 p-3 -mx-3 rounded-lg hover:bg-foreground/5 transition-colors group"
                data-testid="contact-phone-link"
              >
                <div className="w-11 h-11 rounded-lg grid place-items-center bg-[hsl(var(--primary))] text-white flex-shrink-0">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Téléphone</div>
                  <div className="text-base sm:text-lg font-black text-foreground group-hover:text-[hsl(var(--primary))]">
                    {PHONE}
                  </div>
                </div>
              </a>
              <a
                href={`mailto:${EMAIL}`}
                className="flex items-center gap-3 p-3 -mx-3 rounded-lg hover:bg-foreground/5 transition-colors group mt-2"
                data-testid="contact-email-link"
              >
                <div className="w-11 h-11 rounded-lg grid place-items-center bg-[hsl(var(--primary))] text-white flex-shrink-0">
                  <Mail className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Email</div>
                  <div className="text-sm sm:text-base font-bold text-foreground group-hover:text-[hsl(var(--primary))] break-all">
                    {EMAIL}
                  </div>
                </div>
              </a>
              <div className="flex items-center gap-3 p-3 -mx-3 mt-2">
                <div className="w-11 h-11 rounded-lg grid place-items-center bg-foreground/8 text-foreground/60 flex-shrink-0">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Zone</div>
                  <div className="text-sm font-bold text-foreground">Nord Isère · Lyon Est</div>
                  <div className="text-[11px] text-muted-foreground leading-snug">Bourgoin-Jallieu, Villefontaine, La Tour-du-Pin</div>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-[hsl(var(--primary))]/20 bg-[hsl(var(--primary-soft))]/30 p-5">
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-[hsl(var(--primary))] flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-bold text-sm mb-1">Délai de réponse</div>
                  <div className="text-xs text-foreground/70 leading-relaxed">
                    Toute demande reçoit une réponse personnalisée sous 48h ouvrées maximum. Aucune réponse
                    automatique impersonnelle.
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Formulaire devis */}
          <div className="rounded-xl border bg-card p-5 sm:p-7">
            {submitted ? (
              <div className="text-center py-8" data-testid="prestations-form-success">
                <div className="w-16 h-16 rounded-full bg-[hsl(var(--primary-soft))] mx-auto mb-4 grid place-items-center">
                  <CheckCircle2 className="w-8 h-8 text-[hsl(var(--primary))]" />
                </div>
                <h3 className="text-xl font-black mb-2">Demande bien reçue !</h3>
                <p className="text-foreground/70 max-w-md mx-auto">
                  Je vous recontacte personnellement sous <strong>48 heures ouvrées</strong> à l'adresse{' '}
                  <strong>{form.contact_email}</strong> avec un devis adapté.
                </p>
                <Button
                  variant="outline"
                  className="mt-6 rounded-full"
                  onClick={() => {
                    setSubmitted(false);
                    setForm({
                      school_name: '', city: '', structure_type: 'auto-ecole',
                      contact_name: '', contact_email: '', contact_phone: '',
                      needs: '', dates: '', consent: false, website: '',
                    });
                  }}
                  data-testid="prestations-form-new-request"
                >
                  Faire une autre demande
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="prestations-form">
                {/* Honeypot invisible */}
                <input
                  type="text"
                  name="website"
                  autoComplete="off"
                  tabIndex={-1}
                  value={form.website}
                  onChange={(e) => update('website', e.target.value)}
                  style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
                  aria-hidden="true"
                />

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="school_name" className="text-xs font-bold uppercase tracking-wider">
                      Établissement *
                    </Label>
                    <Input
                      id="school_name"
                      required
                      value={form.school_name}
                      onChange={(e) => update('school_name', e.target.value)}
                      placeholder="Ex : Auto-École Dupont"
                      data-testid="form-school-name"
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="city" className="text-xs font-bold uppercase tracking-wider">
                      Ville *
                    </Label>
                    <Input
                      id="city"
                      required
                      value={form.city}
                      onChange={(e) => update('city', e.target.value)}
                      placeholder="Ex : Lyon, Bourgoin, Grenoble…"
                      data-testid="form-city"
                      className="mt-1.5"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="structure_type" className="text-xs font-bold uppercase tracking-wider">
                    Type de structure
                  </Label>
                  <Select value={form.structure_type} onValueChange={(v) => update('structure_type', v)}>
                    <SelectTrigger id="structure_type" data-testid="form-structure-type" className="mt-1.5">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto-ecole">Auto-école (B uniquement)</SelectItem>
                      <SelectItem value="moto-ecole">Moto-école (2 roues uniquement)</SelectItem>
                      <SelectItem value="mixte">Mixte (B + 2 roues)</SelectItem>
                      <SelectItem value="autre">Autre (CFA, centre formation pro…)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="contact_name" className="text-xs font-bold uppercase tracking-wider">
                      Votre nom *
                    </Label>
                    <Input
                      id="contact_name"
                      required
                      value={form.contact_name}
                      onChange={(e) => update('contact_name', e.target.value)}
                      placeholder="Nom du responsable"
                      data-testid="form-contact-name"
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact_phone" className="text-xs font-bold uppercase tracking-wider">
                      Téléphone
                    </Label>
                    <Input
                      id="contact_phone"
                      type="tel"
                      value={form.contact_phone}
                      onChange={(e) => update('contact_phone', e.target.value)}
                      placeholder="04 ...."
                      data-testid="form-contact-phone"
                      className="mt-1.5"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="contact_email" className="text-xs font-bold uppercase tracking-wider">
                    Email professionnel *
                  </Label>
                  <Input
                    id="contact_email"
                    type="email"
                    required
                    value={form.contact_email}
                    onChange={(e) => update('contact_email', e.target.value)}
                    placeholder="contact@ecole.fr"
                    data-testid="form-contact-email"
                    className="mt-1.5"
                  />
                </div>

                <div>
                  <Label htmlFor="needs" className="text-xs font-bold uppercase tracking-wider">
                    Votre besoin *
                  </Label>
                  <Textarea
                    id="needs"
                    required
                    rows={4}
                    value={form.needs}
                    onChange={(e) => update('needs', e.target.value)}
                    placeholder="Ex : remplacement moniteur moto sur 2 semaines, renfort plateau A2, formation 7h Passerelle pour 3 candidats…"
                    data-testid="form-needs"
                    className="mt-1.5"
                  />
                </div>

                <div>
                  <Label htmlFor="dates" className="text-xs font-bold uppercase tracking-wider">
                    Dates souhaitées
                  </Label>
                  <Input
                    id="dates"
                    value={form.dates}
                    onChange={(e) => update('dates', e.target.value)}
                    placeholder="Ex : semaines 24-25, dès que possible, à partir de septembre…"
                    data-testid="form-dates"
                    className="mt-1.5"
                  />
                </div>

                <div className="flex items-start gap-2.5 pt-2">
                  <Checkbox
                    id="consent"
                    checked={form.consent}
                    onCheckedChange={(v) => update('consent', !!v)}
                    data-testid="form-consent"
                    className="mt-0.5"
                  />
                  <Label htmlFor="consent" className="text-xs text-foreground/70 leading-relaxed cursor-pointer">
                    J'accepte que Bruno Llorens (Code 2 Roues) utilise ces informations pour me recontacter
                    dans le cadre de cette demande de devis. Aucune utilisation commerciale ni transmission à
                    des tiers.
                  </Label>
                </div>

                {error && (
                  <div className="rounded-lg border border-red-300 bg-red-50 text-red-900 px-4 py-3 text-sm flex items-start gap-2" data-testid="form-error">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>{error}</span>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={submitting}
                  size="lg"
                  className="w-full h-12 rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 font-bold"
                  data-testid="form-submit"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Envoi en cours…
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Envoyer ma demande
                    </>
                  )}
                </Button>

                <p className="text-[11px] text-muted-foreground italic text-center">
                  Vos données sont stockées de manière sécurisée. Aucun spam, aucune revente.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* ===== SIGNATURE FINALE — slogan baseline ===== */}
      <section className="text-center pt-4" data-testid="prestations-baseline">
        <div className="inline-block">
          <div className="text-[10px] sm:text-xs font-black uppercase tracking-[0.3em] text-[hsl(var(--primary))] mb-2">
            Code 2 Roues · Bruno Llorens
          </div>
          <div
            className="text-base sm:text-lg font-black text-foreground/80"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            La route, notre passion — <span className="text-[hsl(var(--primary))]">votre réussite, notre mission.</span>
          </div>
        </div>
      </section>

      {/* ===== MODAL DÉTAILS PRESTATION (composant partagé) ===== */}
      <PrestationDialog
        prestation={selectedPrestation ? toPrestation(selectedPrestation) : null}
        onClose={() => setSelectedPrestation(null)}
      />
    </div>
  );
}

